import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import '../../components/tracker/Tracker.css';
import '../../components/tracker/PlayerDashboard.css';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function quickPitchingLine(p) {
  if (!p) return null;
  const parts = [];
  if (p.ip != null) parts.push(`${p.ip} IP`);
  if (p.k != null) parts.push(`${p.k} K`);
  if (p.bb != null) parts.push(`${p.bb} BB`);
  if (p.h != null) parts.push(`${p.h} H`);
  if (p.er != null) parts.push(`${p.er} ER`);
  return parts.join(' · ');
}

function quickBattingLine(b) {
  if (!b) return null;
  const parts = [];
  if (b.h != null && b.ab != null) parts.push(`${b.h}/${b.ab}`);
  if (b.hr) parts.push(`${b.hr} HR`);
  if (b.rbi != null) parts.push(`${b.rbi} RBI`);
  if (b.bb) parts.push(`${b.bb} BB`);
  if (b.sb) parts.push(`${b.sb} SB`);
  return parts.join(' · ');
}

function quickFieldingLine(f) {
  if (!f || !Array.isArray(f) || f.length === 0) return null;
  const totals = f.reduce(
    (acc, pos) => {
      acc.po += pos.po || 0;
      acc.a += pos.a || 0;
      acc.e += pos.e || 0;
      return acc;
    },
    { po: 0, a: 0, e: 0 }
  );
  const positions = f.map((pos) => pos.position).join(', ');
  return `${positions} · ${totals.po} PO · ${totals.a} A · ${totals.e} E`;
}

export default function GameHistoryPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // player id
  const [player, setPlayer] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resumeModalGame, setResumeModalGame] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [playerRes, gamesRes] = await Promise.all([
          api.get(`/analytics/players/${id}`),
          api.get(`/analytics/players/${id}/games`),
        ]);
        setPlayer(playerRes.data);
        setGames(gamesRes.data || []);
      } catch (err) {
        console.error('Failed to load game history:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  async function handleDelete(gameId) {
    setDeleting(true);
    try {
      await api.delete(`/analytics/players/${id}/games/${gameId}`);
      setGames((prev) => prev.filter((g) => g.id !== gameId));
      setDeleteId(null);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="dash-page">
      <div className="analytics-page-wrap">
        {/* Header */}
        <div className="analytics-page-header">
          <button
            className="analytics-back-btn"
            onClick={() => navigate('/analytics/players')}
          >
            ← Players
          </button>
          <h1 className="analytics-page-title">
            {player ? `${player.firstName} ${player.lastName}` : 'Game History'}
          </h1>
          <button
            className="analytics-add-btn"
            onClick={() => navigate('/analytics/tracker')}
          >
            + Game
          </button>
        </div>

        {loading ? (
          <div className="analytics-empty">Loading…</div>
        ) : games.length === 0 ? (
          <div className="analytics-empty">
            <div className="analytics-empty-icon">📋</div>
            <div className="analytics-empty-text">No games recorded yet.</div>
            <button
              className="analytics-empty-btn"
              onClick={() => navigate('/analytics/tracker')}
            >
              Track First Game
            </button>
          </div>
        ) : (
          <div className="game-list">
            {games.map((game) => (
              <div key={game.id} className="game-card">
                <div
                  className="game-card-main"
                  onClick={() =>
                    game.status === 'in_progress'
                      ? setResumeModalGame(game)
                      : navigate(`/analytics/players/${id}/games/${game.id}`)
                  }
                >
                  <div className="game-card-top">
                    <div className="game-card-date">
                      {formatDate(game.gameDate)}
                    </div>
                    <div className="game-card-opponent">
                      vs {game.opponent || '—'}
                    </div>
                    <div className={`dash-game-status ${
                      game.status !== 'in_progress'
                        ? 'completed'
                        : (game.batting || game.pitching || game.fielding)
                          ? 'in-progress'
                          : 'no-stats'
                    }`}>
                      {game.status !== 'in_progress'
                        ? 'Completed'
                        : (game.batting || game.pitching || game.fielding)
                          ? 'In Progress (Unsaved stats)'
                          : 'No Stats Entered'}
                    </div>
                    {(game.pitching?.teamScore != null ||
                      game.batting?.teamScore != null) && (
                      <div className="game-card-score">
                        {game.teamScore ?? '—'} – {game.opponentScore ?? '—'}
                      </div>
                    )}
                  </div>

                  <div className="game-card-stats">
                    {game.pitching && (
                      <div className="game-stat-line">
                        <span className="game-stat-tag pitching">P</span>
                        <span className="game-stat-text">
                          {quickPitchingLine(game.pitching)}
                        </span>
                      </div>
                    )}
                    {game.batting && (
                      <div className="game-stat-line">
                        <span className="game-stat-tag batting">B</span>
                        <span className="game-stat-text">
                          {quickBattingLine(game.batting)}
                        </span>
                      </div>
                    )}
                    {game.fielding && (
                      <div className="game-stat-line">
                        <span className="game-stat-tag fielding">F</span>
                        <span className="game-stat-text">
                          {quickFieldingLine(game.fielding)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  className="game-delete-btn"
                  onClick={() => setDeleteId(game.id)}
                  title="Delete game"
                >
                  🗑
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Delete this game?</h3>
            <p>
              This will permanently delete the game and all its stats. This
              cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={() => handleDelete(deleteId)}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete Game'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume confirmation modal */}
      {resumeModalGame && (
        <div className="modal-overlay" onClick={() => setResumeModalGame(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Resume this game?</h3>
            <p>
              This game is still in progress. Continue tracking it live, or just view the stats recorded so far.
            </p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setResumeModalGame(null)}>
                Cancel
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate(`/analytics/players/${id}/games/${resumeModalGame.id}`)}
              >
                View Stats Only
              </button>
              <button
                className="save-btn"
                onClick={() => navigate('/analytics/tracker', { state: { preselectedPlayerId: id, resumeGameId: resumeModalGame.id } })}
              >
                Resume Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
